import {
	faCar as farCar,
	faMoneyBillWave as farMoneyBillWave,
	faPaintRoller as farPaintRoller,
	faQuestion as farQuestion,
} from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faCalculator as fasCalculator,
	faFileSignature as fasFileSignature,
	faMoneyBill as fasMoneyBill,
	faPhone as fasPhone,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { PersonalLoanCalculatorV2 } from "@component/PersonalLoan/CalculatorV2/PersonalLoanCalculator";
import type { FAQSchema } from "@component/StructuredDataSchema";
import { StructuredDataSchema } from "@component/StructuredDataSchema";
import { YoutubeVideo } from "@component/Widget/YoutubeVideo/YoutubeVideo";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BankLogosCloud } from "@landing/components/BankLogosCloud";
import { LandingAccordion } from "@landing/components/LandingAccordion";
import { LandingBottom } from "@landing/components/LandingBottom";
import { ExpertSlider } from "@ui/components/ExpertSlider";
import { formatText } from "@ui/format";
import { currentYear } from "@utils/current-year";
import { FeatureBox, LandingContainer, LandingHowTo, LandingHowToItem, List } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Accordion, Col, Row } from "react-bootstrap";

/**
 * @Routing
 *
 * @title "Személyi Kölcsön Kalkulátor [YEAR]"
 * @description "Személyi hitel ajánlatok ingyenes, regisztrációmentes összehasonlítása. Szabad felhasználású, fix kamatozású személyi kölcsön, akár online igényléssel."
 * @route "/szemelyi-kolcson/"
 */
export default class Landing extends React.PureComponent {
	public render(): ReactNode {
		return (
			<>
				<PersonalLoanCalculatorV2
					title={
						<>
							<span className="d-md-none">Személyi kölcsön kalkulátor {currentYear()}</span>
							<span className="d-none d-md-inline">
								Személyi kölcsön kalkulátor feltételek & igénylés
							</span>
						</>
					}
					subTitle="Segítünk, amíg a pénzt megkapod!"
					income={-1}
					loanAmount={-1}
				/>
				{this.renderMainProperties()}
				{this.renderThisIsHowYoullGetTheLoan()}
				<LandingContainer>
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Szakértőink, akik Neked segítenek</h2>
					<ExpertSlider groupName="BM_HQ_PERSONAL_LOAN" />
				</LandingContainer>
				<LandingContainer id="tovabbi-szemelyi-kolcson-kalkulatorok" sendEvent bgClassName="bg-gray">
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">
						További <mark className="bg-light text-secondary">személyi kölcsön</mark> kalkulátorok
					</h2>
					{this.renderCards()}
				</LandingContainer>
				{this.renderPersonalLoanRequestConditions()}
				{this.renderWhatToKnow()}
				{this.renderUsefulTips()}
				{this.renderBmHelp()}
				{this.renderFaq()}
				{this.renderFogalomTar()}
				{this.renderUsefulLinks()}
				<LandingContainer>
					<BankLogosCloud
						variant="personalLoan"
						title="Személyi kölcsön kalkulátorunk az alábbi bankok termékeit tartalmazza"
					/>
				</LandingContainer>
				{this.renderVideos()}
				<LandingBottom tags="szemelyi-kolcson" />

				<StructuredDataSchema schema={this.getSchema()} />
			</>
		);
	}

	private renderThisIsHowYoullGetTheLoan(): ReactNode {
		return (
			<LandingContainer bgClassName="bg-gray">
				<LandingHowTo title="Így kapod meg a személyi kölcsönt">
					<LandingHowToItem title="Kalkulálj" icon={<FontAwesomeIcon icon={fasCalculator} />}>
						Add meg mekkora hitelösszegre van szükséged és megmutatjuk a legkedvezőbb személyi kölcsön
						ajánlatokat.
					</LandingHowToItem>
					<LandingHowToItem title="Kérj visszahívást" icon={<FontAwesomeIcon icon={fasPhone} />}>
						Megválaszoljuk a kérdéseid és megtudhatod, hogy előreláthatólag mekkora hitelre számíthatsz.
					</LandingHowToItem>
					<LandingHowToItem title="Szerződéskötés" icon={<FontAwesomeIcon icon={fasFileSignature} />}>
						A szerződést közvetlenül a bankkal kötöd, ahonnan ezután átutalják számodra a pénzt.
					</LandingHowToItem>
					<LandingHowToItem title="Összeg folyósítása" icon={<FontAwesomeIcon icon={fasMoneyBill} />}>
						A kiválasztott bank folyósítja az általad igényelt hitelösszeget.
					</LandingHowToItem>
				</LandingHowTo>
			</LandingContainer>
		);
	}

	private renderMainProperties(): ReactNode {
		return (
			<LandingContainer size="lg" sendEvent>
				<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Személyi kölcsön legfőbb jellemzői</h2>
				<Row className="align-items-center">
					<Col md="6">
						<List itemStyle="tick" variant="secondary">
							<List.Item>
								<strong>2-5 munkanap</strong> alatt a számládon lehet a pénz
							</List.Item>
							<List.Item>
								<strong>Online is igényelheted a Bankmonitoron keresztül</strong>
							</List.Item>
							<List.Item>
								<strong>Szakértőink részletes felvilágosítást adnak</strong>
								<br />a feltételekről és a termékek közötti különbségekről
							</List.Item>
						</List>
					</Col>
					<Col md="6">
						<List itemStyle="tick" variant="secondary">
							<List.Item>
								<strong>Ingatlanfedezet nélkül</strong> felvehető hitel{" "}
							</List.Item>
							<List.Item>
								<strong>Igazolt jövedelemre</strong> van szükség a hitel felvételéhez{" "}
							</List.Item>
							<List.Item>
								A <strong>kamat</strong> a felvett hitel összeg és az igazolt jövedelem nagyságától függ
							</List.Item>
						</List>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderPersonalLoanRequestConditions(): ReactNode {
		return (
			<LandingContainer id="szemelyi-kolcson-igenylesenek-feltetelei" sendEvent>
				<Row className="justify-content-center">
					<Col md="10">
						<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">
							Személyi kölcsön feltételei {currentYear(true)}
						</h2>
						<Accordion>
							<LandingAccordion title="Alapvető igénylési feltételek">
								<p>
									A személyi hitel alapvető feltételei minden banknál hasonlóak, ugyanakkor az egyes
									bankok a minimális elvárásokon túl szigorúbb feltételeket is szabhatnak. Ha az
									alábbi, alap személyi hitel feltételeknek eleget tudsz tenni, akkor már jó eséllyel
									kaphat zöld utat az igénylésed.
								</p>
								<h4>1. Milyen minimális jövedelem szükséges?</h4>
								<p>
									Minden pénzintézet meghatároz egy minimálisan elvárt jövedelmi szintet, az
									igényléshez legalább ezt az összeget el kell érnie a fizetésednek. Ez{" "}
									<strong>jellemzően a minimálbér szokott lenni:</strong> {currentYear(true)} a
									minimálbér nettó összege {formatText(Config.getMinWage(), "ft")}.
								</p>
								<h4>2. Milyen hosszú munkaviszony szükséges?</h4>
								<p>
									Legalább <strong>3-6 hónapja a jelenlegi munkahelyeden kell dolgoznod</strong>{" "}
									ahhoz, hogy személyi kölcsönt tudj igényelni. Továbbá nem lehetsz a hiteligénylés
									során próbaidőn.
								</p>
								<p>
									Ha határozott idejű munkaviszonnyal rendelkezel, akkor a személyi kölcsön feltételek
									még szigorúbbak:
								</p>
								<ul>
									<li>Jellemzően legalább 0,5-1 éves munkaviszonyt várnak el a bankok</li>
									<li>
										Ráadásul a határozott idő periódusa sem lényegtelen, több bank legalább fél éves
										periódust vár el.
									</li>
									<li>
										Ha pedig közel van a szerződés lejárata, akkor gyakran kérnek be a munkáltatótól
										szándéknyilatkozatot a foglalkoztatás meghosszabbításáról.
									</li>
								</ul>
								<p>
									De vállalkozóként sem lehetsz könnyű helyzetben. Ha személyi hitelt szeretnél, akkor
									a bankok jellemzően egy éves vállalkozói múltat várnak el, sőt, sok esetben egy
									teljes lezárt üzleti évet is igazolnod kell.
								</p>
								<p>
									Amit biztosan ki lehet jelenteni, hogy munkanélküliként, fix és igazolt jövedelem
									nélkül jelenleg nem tudsz hitelt igényelni. (Ez általánosságban is igaz, nem csak a
									személyi kölcsönökre.)
								</p>
								<h4>3. Nem szerepelhetsz a KHR listán</h4>
								<p>
									<strong>
										Személyi kölcsön igényléskor nem szerepelhetsz a rossz adósokat tartalmazó
										negatív{" "}
										<a href="/jobb-tudni/szemelyi-kolcson/?link_type=landing-faq#khr-lista-bar-lista">
											KHR listán
										</a>
										.
									</strong>{" "}
									(Akkor kerülhetsz fel erre a listára, ha legalább 90 napja a mindenkori minimálbért
									meghaladó elmaradásod van a hiteleddel kapcsolatban. Illetve felkerülhetsz a listára
									visszaélés, csalás miatt is.)
								</p>
								<p>
									A rossz adósokat tartalmazó listából kétféle van. Ha az elmaradásodat még nem
									rendezted, akkor a negatív KHR listán aktív státusszal szerepelsz. Ebben az esetben
									biztos, hogy nem kaphatsz kölcsönt. Ha viszont tartozásodat már rendezted, akkor
									átkerülsz passzív státuszba. Sajnos a legtöbb bank így sem fog személyi kölcsönt
									nyújtani neked. Azonban jó hír, hogy a passzív listáról – amennyiben rendezted az
									elmaradásodat - egy év múlva lekerülsz és ezt követően nem lesz nyoma a korábbi
									tartozásodnak.{" "}
								</p>
								<p>
									Természetesen a pozitív KHR listán szerepelhetsz, ez nem akadálya a
									kölcsönigénylésnek. Ez érthető is, hiszen oda valamennyi hitellel rendelkező adós
									felkerül függetlenül attól, hogy rendben fizeti-e a kölcsöne törlesztőrészletét.
								</p>
								<h4>4. Megfelelő bankszámlamúlt</h4>
								<p>
									Legalább 2-3 hónapos bankszámlamúlttal kell rendelkezned egy magyarországi
									pénzintézetnél. A számlád kivonatait be fogja kérni a hitelt nyújtó pénzintézet:
									jellemzően az utolsó 2-3 kivonatra lesz szükség.
								</p>
								<p>
									Jellemzően annak a számlának a kivonatát szükséges benyújtanod, amelyre a fizetésed
									is érkezik.
								</p>
								<h4>5. Életkor</h4>
								<p>
									Személyi hitel igénylésekor nagykorúnak kell lenned, egyes bankok azonban ennél még
									szigorúbbak is: nem nyújtanak hitelt 21-24 évnél fiatalabbaknak.
								</p>
								<p>
									A kölcsön lejáratakor nem lehetsz idősebb 65-70 évesnél. (Eltérő lehet a maximális
									életkor is az egyes bankok esetében.) Ez azt is jelenti, hogy egyes idősebb
									érdeklődők csak rövidebb futamidőre vehetnek fel hitelt.
								</p>
								<p>
									Az életkori korlátok alól több esetben könnyítést adnak a bankok abban az esetben,
									ha az adós mellé egy minden feltételnek megfelelő{" "}
									<a href="/jobb-tudni/szemelyi-kolcson/?link_type=landing-faq#adostars-vagy-tarsigenylo">
										társigénylő, vagy adóstárs
									</a>{" "}
									is bekerül a hitelügyletbe. Ez azt is jelenti, hogy a másik fél is teljes
									felelősséggel tartozik a felvett kölcsön visszafizetéséért, vagyis ha az igénylő nem
									rendezi a havi törlesztőrészletet, akkor az adóstársnak kell kifizetnie az
									elmaradást.
								</p>
								<h4>6. Magyarországi lakcím</h4>
								<p>
									A pénzintézetek elvárják, hogy az adós rendelkezzen állandó magyarországi lakcímmel.
									Ez a lakcímkártyán szereplő lakóhelynek felel meg.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Milyen egyéb jövedelmi feltételeknek kell eleget tenni?">
								<p>
									Természetesen a minimális feltételek teljesítése nem azt jelenti, hogy meg is
									kaphatod a kölcsönt. A jövedelmednek elégségesnek kell lennie a hitel
									törlesztőrészletének megfizetéséhez. (Ez érthető is, hiszen a minimálbérből nem
									tudsz kifizetni havi 100-150 ezer forintot. De az sem megfelelő a pénzintézetek
									számára, ha a fizetésed éppen megegyezik a kölcsön törlesztőrészletével.)
								</p>
								<p>
									Ráadásul{" "}
									<strong>
										a jogszabály is meghatározza azt, hogy a fizetésed hány százalékát fordíthatod
										hiteltörlesztésre.
									</strong>{" "}
									Ezt a számítási módot nevezik{" "}
									<a href="/jobb-tudni/babavaro/?link_type=landing-faq#jovedelemaranyos-torlesztoreszlet-mutato-jtm">
										JTM szabálynak
									</a>
									, vagy jövedelemarányos törlesztőrészlet mutatónak is.
								</p>

								<p>
									Fontos, hogy a számítás során figyelembe veszik az új személyi hitel
									törlesztőrészlete mellett a már meglévő kölcsönökre fizetendő havi összeget is. Sőt,
									a meglévő hitelkeretek – hitelkártya, folyószámlahitel –{" "}
									<strong>teljes keretének 5 százaléka is törlesztőrészletnek minősül</strong>{" "}
									függetlenül attól, hogy a keretet kihasználtad-e vagy sem.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Milyen típusú jövedelmeket fogadnak el a bankok?">
								<p>
									Az egyes bankok bírálati gyakorlata eltérő lehet, így teljesen más módon kezelik az
									egyes pénzintézetek a speciális jövedelmi formákat is. Ezek az eltérések nagyon is
									lényegesek lehetnek: elképzelhető, hogy egyik pénzintézet elutasítja a személyi
									kölcsön kérelmedet, míg egy másik megadná a kért hitelösszeget. Abban tehát, hogy
									melyik számodra a legjobb bank, segíthet egy online személyi kölcsön kalkulátor és
									egy független hitelügyintéző.
								</p>
								<p>
									Például teljesen másképp kezelik a katás vállalkozók - vagy volt katás vállalkozók -
									bevételét az egyes bankok.
								</p>
								<p>
									Eltérő feltételekkel és módon fogadják el a bankok a külföldről származó
									jövedelmeket is. A legtöbb pénzintézet csak meghatározott országokból származó
									jövedelmet fogad el, ez az országlista pedig különböző az egyes pénzintézetek
									esetében. Sőt, az elvárt minimális munkaviszony, jövedelem is különböző módon lehet
									kezelve az egyes bankoknál.
								</p>
								<p>
									A kismamák fizetését is másképp fogadják el a bankok, jellemzően csak kiegészítő
									fizetésként. Így nem mindegy, hogy a családi pótlék, a GYES, GYED, GYET összegével
									nem számol teljes értékűen a pénzintézet az adható hitelösszeg meghatározása során.
								</p>
								<p>
									A bankok közötti eltérések miatt még lényegesebb, hogy körültekintően, több
									lehetőséget, bankot is megismerve válaszd ki a számodra megfelelő ajánlatot.
								</p>
							</LandingAccordion>
						</Accordion>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderWhatToKnow(): ReactNode {
		return (
			<LandingContainer id="mit-kell-tudni-a-szemelyi-kolcsonrol" sendEvent>
				<Row className="justify-content-center">
					<Col md="10">
						<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Mit kell tudni a személyi kölcsönről?</h2>
						<Accordion>
							<LandingAccordion title="Milyen célra vehető fel személyi hitel?">
								<p>
									A személyi hitelek a felhasználás célja szerint is megkülönböztethetők. Az igényelt
									összeget számos célra felhasználhatod.
								</p>
								<p>
									<strong>
										<a href="/felujitasi-hitel/?link_type=landing-faq">
											Lakásfelújítás,korszerűsítés:
										</a>
									</strong>{" "}
									otthonod modernizálását, korszerűsítését is megfinanszírozhatod a hitel összegéből.
									Ezzel élhetőbbé teheted otthonod és akár a rezsit is csökkentheted.
								</p>
								<p>
									<strong>Autó, gépjármű vásárlás:</strong> tönkrement az autód, vagy csak lecserélnéd
									meglévő gépjárműved? <a href="/autohitel/?link_type=landing-faq">Autóvásárlás</a>{" "}
									illetve autójavítás céljára is felhasználhatod a kapott összeget. Egy személyi
									kölcsön viszonylag gyorsan igényelhető, így könnyen jó alkupozícióba kerülhetsz
									autóvásárláskor.
								</p>
								<p>
									<strong>
										<a href="/adossagrendezo-hitel/?link_type=landing-faq">
											Hitelkiváltás, vagy adósságrendezés:
										</a>
									</strong>{" "}
									meglévő kölcsönöd biztonságosabbra, olcsóbbra is cserélheted. Egy hitelkiváltásnál a
									meglévő hiteled kifizetésre kerül az újonnan igényelt személyi kölcsönből. Ilyenkor
									a meglévő kölcsön fizetési kötelezettségét nem veszik figyelembe, amikor az
									igényelhető összeg nagyságát határozzák meg a bankok. Nem elvárás az, hogy az új
									kölcsönt attól a banktól vedd fel, amelynél a meglévő hiteled is van. (De
									természetesen ez a lehetőség sincs kizárva, a hiteled lecserélésével nem kell
									feltétlenül bankot váltanod.)
								</p>
								<p>
									Adósságrendezésről személyi kölcsön kapcsán akkor beszélhetünk, ha több tartozást is
									szeretnél visszafizetni (például áruhitelt, folyószámlahitelt, autóhitelt,
									hitelkártya tartozást, gyorskölcsönt, mini kölcsönt). Ha személyi hitelt vennél fel
									adósságrendezésre, akkor azon felül, hogy csökkentheted havi kiadásaidat, a jövőben
									sokkal átláthatóbb és követhetőbb lesz a törlesztés, hiszen csak egy bank felé
									leszel elköteleződve.
								</p>
								<p>
									<strong>Lakásvásárlás:</strong> akár az ingatlanvásárlást is megfinanszírozhatod
									személyi kölcsönből. Azt mindenképpen ki kell hangsúlyozni, hogy a lakáshitel mellé
									minimálisan elvárt önerőt nem teremtheted elő személyi hitelből.{" "}
								</p>
								<p>
									<strong>Szabad felhasználás:</strong> a szabad cél választásával nem határozod meg a
									bank felé, hogy a kapott pénzt mire szeretnéd elkölteni. Az összegről szabadon
									rendelkezhetsz, a fenti célok bármelyikére elköltheted, de ettől eltérő céljaidat is
									megvalósíthatod.
								</p>
								<p>
									<strong>Joggal merülhet fel a kérdés:</strong> miért érné meg a szabad célon kívül
									mást választanod? Hiszen ezt jelölve bármit kifizethetsz a kölcsönből. Ez igaz is,
									ugyanakkor egyes pénzintézetek meghatározott cél választásakor kedvezőbb
									kondíciókkal kínálnak személyi hitelt. Mások pedig a feltételeken egyszerűsítenek
									bizonyos konstrukciók esetében. Éppen ezért lényeges lehet átgondolni, hogy mire
									költenéd el a kölcsönt és ezt a pénzintézet számára is érdemes pontosan megadni.
								</p>
								<p>
									Emellett gyakorta előfordul, hogy nem csak egy célt szeretnél megvalósítani, például
									szeretnéd visszafizetni meglévő tartozásod, de lakáskorszerűsítésre is költenél az
									igényelt összegből. Ilyen kombinált esetben is a szabad felhasználás lesz az ideális
									hitelcél.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Szükséges a személyi kölcsönhöz ingatlanfedezet?">
								<p>
									A személyi hitel egy fedezet nélküli kölcsön, ez azt is jelenti, hogy a
									konstrukcióhoz nem szükséges biztosítékként házat, lakást felajánlani. Éppen ezért
									ideális megoldás lehet számodra, ha nem rendelkezel ingatlannal, vagy a lakásod,
									házad már jelzáloggal terhelt (például lakáshitel igénylés miatt).
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Mennyi személyi hitelt vehetek fel?">
								<p>
									Személyi kölcsönt már 500 ezer forinttól igényelhetsz. A konstrukció maximális
									összege akár 15 millió forint is lehet, vagyis akár egy nagyobb összegű kiadást is
									fedezhetsz a kapott pénzből.
								</p>
								<p>
									Ugyanakkor egy 0-12 millió forintot meghaladó kiadásra már nem biztos, hogy ez a
									hiteltípus lesz az ideális megoldás. Például érdemes a{" "}
									<a href="/lakashitel-kalkulator/?link_type=landing-faq">lakáshitelek</a> vagy a
									szabad felhasználású jelzáloghitelek között szétnézned. Ha szeretnéd megtudni, hogy
									a jövedelemeddel legfeljebb mekkora személyi kölcsönt vehetsz fel, használd a
									Bankmonitor{" "}
									<a href="/mennyi-szemelyi-kolcsont-kaphatok/?link_type=landing-faq">
										“Mennyi kölcsönt kaphatok”
									</a>{" "}
									kalkulátorát.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Milyen hosszú futamidőre vehetem fel a kölcsönt?">
								<p>
									Az alacsonyabb maximális összegnek is köszönhetően a futamidő nem haladhatja meg a
									10 évet. Ráadásul ilyen hosszú futamidőre nem találhatunk az összes banknál
									ajánlatot. A legtöbb banknál 7-8 év lehet a leghosszabb futamidő.
								</p>
								<p>
									Természetesen ennél jóval rövidebb idő alatt is visszafizethető a tartozás, akár egy
									éves futamidőt is választhatsz. A rövid futamidő esetén ugyan a törlesztőrészlet
									emelkedne, de összességében kevesebbet fizetnél a bank részére. Vagyis csökkenne a
									teljes visszafizetendő összeg, hiszen kevesebb ideig használnád a bank pénzét.
								</p>
								<p>
									A Bankmonitor személyi kölcsön kalkulátorában van lehetőséged megadni, hogy milyen
									futamidőre szeretnéd felvenni a hitelt. Ennek változtatásával finomíthatsz a
									találatokon: ha úgy látod, hogy túl magas a törlesztőrészlet, akkor hosszabb
									futamidőt válassz.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Melyik a legjobb bank? Hol érdemes igényelni?">
								<p>
									A személyi kölcsön kalkulátorral egyszerűen összehasonlíthatod, hogy melyik bankok
									kínálják a legolcsóbb hiteleket. A legjobb és legkedvezőtlenebb ajánlatok között
									jelentős a különbség. 2025-ben találni személyi hitelt már 10% alatti kamattal, 12%
									alatti teljes hiteldíj mutató (THM) mellett is, de olyan konstrukció is akad, ahol
									az éves teljes költségek meghaladhatják a 26%.
								</p>
								<p>
									Természetesen a legjobb banki ajánlatok elnyeréséhez néhány feltételt teljesítened
									kell. Ezek az elvárások vonatkozhatnak:
								</p>
								<ul>
									<li>jövedelmed nagyságára</li>
									<li>vállalt havi jóváírásra</li>
									<li>hitelösszeg nagyságára</li>
									<li>
										kapcsolódó szolgáltatásokra: hitelfedezeti életbiztosítás, bankszámlavezetés…
									</li>
								</ul>
								<p>
									Éppen ezért alaposan érdemes szétnézni a piacon a kölcsönigénylés előtt. Meg kell
									találnod azt a legolcsóbb konstrukciót, amelyhez kapcsolódóan az akciós feltételeket
									is teljesíteni tudod.
								</p>
								<p>
									A Bankmonitor hitelkalkulátora jelenleg 8 hazai nagybank ajánlatait tartalmazza. Az
									egyes bankok ajánlatait és összehasonlításait a bankok nevére kattintva is van
									lehetőséged megtekinteni:
								</p>
								<ul>
									<a href="/otp/szemelyi-kolcson/?link_type=landing-faq">
										<li>OTP személyi kölcsön kalkulátor</li>
									</a>
									<a href="/kh/szemelyi-kolcson/?link_type=landing-faq">
										<li>K&H személyi kölcsön kalkulátor</li>
									</a>
									<a href="/unicredit/szemelyi-kolcson/?link_type=landing-faq">
										<li>UniCredit Bank személyi hitel kalkulátor</li>
									</a>
									<a href="/mbh/szemelyi-kolcson/?link_type=landing-faq">
										<li>MBH Bank személyi kölcsön kalkulátor</li>
									</a>
									<a href="/cib/szemelyi-kolcson/?link_type=landing-faq">
										<li>CIB személyi kölcsön</li>
									</a>
									<a href="/erste/szemelyi-kolcson/?link_type=landing-faq">
										<li>Erste személyi kölcsön hitelkalkulátor</li>
									</a>
									<a href="/raiffeisen/szemelyi-kolcson/?link_type=landing-faq">
										<li>Raiffeisen személyi kölcsön kalkulátor</li>{" "}
									</a>
								</ul>
								<p>
									A kalkulátorban a legjobb ajánlat kiválasztása után lehetőséged van online
									elindítani az igénylési folyamatot. Az adatok elküldése után személyi kölcsön
									szakértőnk felveszi veled a kapcsolatot, megválaszolja kérdéseid és végig segít,
									hogy az igénylési folyamat egyszerűbb és gördülékenyebb legyen.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Személyi hitel fix kamattal">
								<p>
									A legtöbb személyi kölcsön jelenleg fix kamattal igényelhető, vagyis nem kell
									tartanod a kamatváltozás kockázatától, hogy a kölcsön törlesztőrészlete a futamidő
									alatt megemelkedik.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Minimális induló költség mellett elérhető hitel">
								<p>
									A személyi hitel igénylésekor megfizetendő díj relatív alacsony. Maximum 1
									százalékos folyósítási díjat terhelnek be a bankok a kölcsön kifizetésekor, vagyis a
									hitelösszeg 1 százaléka lehet az induló díj. Ugyanakkor számos pénzintézet még ettől
									a tételtől is eltekint, a legtöbb kölcsön induló költség nélkül igényelhető
									jelenleg. (Egy lakáshitellel összehasonlítva az eltérés jelentős: egy jelzáloghitel
									igénylésekor akár több százezer forintot is kell fizetned.)
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="A THM (teljes hiteldíj mutató) értéke">
								<p>
									A hitelek összehasonlításában fontos szerepe van a{" "}
									<a href="/jobb-tudni/babavaro/#teljes-hiteldij-mutato-thm/?link_type=landing-faq">
										THM-nek (teljes hiteldíj mutató),
									</a>{" "}
									amely százalékos formában számszerűsíti az adott hitel teljes éves költségét. Mivel
									a THM értékét befolyásolja a választott futamidő, az igényelt hitelösszeg és az
									igazolt nettó jövedelem nagysága, a kölcsön paramétereinek ismeretében lehet csak
									kiszámítani a pontos értékét.
								</p>
								<p>
									A Bankmonitor személyi kölcsön kalkulátor számításai alapján 450 000 Ft nettó
									igazolt jövedelemmel így alakulnak a minimális és maximális THM-ek a vizsgált
									termékek esetében 2025. januárjában:
								</p>
								<ul>
									<li>
										2 000 000 Ft hitelösszeg, 5 éves futamidő: THM 12,75 – 22,69% ahol a legjobb
										banki ajánlat az UniCredit Bank fix kamat személyi kölcsön, a legkedvezőtlenebb
										az OTP Bank személyi kölcsön konstrukciója.
									</li>
									<li>
										4 000 000 Ft hitelösszeg, 5 éves futamidő: THM 10,84-21,47% ahol a legkedvezőbb
										személyi kölcsön szintén a Raiffeisen Banké, az utolsó a listán pedig szintén az
										OTP Bank szabad felhasználású személyi hitele.
									</li>
									<li>
										6 000 000 Ft hitelösszeg, 5 éves futamidő: THM 10,84 – 19,06% itt a dobogó
										legfelső fokára ugyancsak az Raiffeisen személyi kölcsön ajánlata, míg az utolsó
										helyre szintén az OTP Bank személyi kölcsöne futott be.
									</li>
								</ul>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Személyi kölcsön főbb jellemzői">
								<ul>
									<li>Hitelösszeg: 800 ezer Ft - 15 millió Ft</li>
									<li>Futamidő: 1 év - 10 év</li>
									<li>Kamat: 9,90%* - 22,99%**</li>
									<li>THM: 10,72%* - 26,43%**</li>
								</ul>
								<p>
									*A Bankmonitor kalkulátora alapján 5 millió Ft hitelösszeg, 7 éves futamidő és havi
									1,2 millió Ft nettó jövedelem beállítása esetén.
								</p>
								<p>
									** A Bankmonitor kalkulátora alapján 800 ezer Ft hitelösszeg, 5 éves futamidő és
									havi {formatText(Config.getMinWage(), "ft")} nettó jövedelem beállítása esetén.
								</p>
							</LandingAccordion>
						</Accordion>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderUsefulTips(): ReactNode {
		return (
			<LandingContainer id="hasznos-tippek-a-szemelyi-kolcson-felvetelehez" sendEvent>
				<Row className="justify-content-center">
					<Col md="10">
						<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">
							Hasznos tippek személyi kölcsön felvételhez
						</h2>
						<Accordion>
							<LandingAccordion title="Keresd meg a számodra legolcsóbb ajánlatot!">
								<p>
									Azt mindenki jó eséllyel tudja, hogy mennyi pénzre lenne szüksége és mire költené el
									az összeget. Mégis érdemes ezek átgondolására is időt fordítani. A hitelösszeg
									például befolyásolhatja a kamat nagyságát is, elképzelhető, hogy egy kicsivel
									magasabb összeg igénylése miatt a kölcsön kamata csökkenne és összességében jobban
									járnál, mintha kevesebb hitelt igényeltél volna.
								</p>
								<p>
									A legtöbben azt gondolják, hogy a személyi hitel szabad felhasználású kölcsön, ezért
									bármire elkölthető a kapott összeg. Ez alapvetően igaz is, hiszen van szabad
									felhasználású személyi kölcsön is, melyet tetszőleges célra lehet fordítani. Ettől
									függetlenül a megfelelő hitelcél meghatározása fontos lehet, ugyanis egyes speciális
									célokra külön, egyedi hitelt ajánlanak a bankok. Ezen kölcsönök pedig gyakran
									kedvezőbbek – alacsonyabb kamat, magasabb igényelhető összeg, egyszerűbb feltételek
									-, mint a szabad célú konstrukciók.{" "}
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Használd hitelkalkulátorunkat a legjobb személyi kölcsön kiválasztásához!">
								<p>
									A Bankmonitor személyi kölcsön kalkulátorának használatával időt és pénzt is
									spórolhatsz. A hitelkalkulátor megmutatja a legjobb banki ajánlatokat, ráadásul
									teljesen személyre szabottan, az általad megadott adatok alapján, rangsorolva
									jelennek meg az egyes konstrukciók.
								</p>
								<p>
									Így a kalkulátor által adott lista a megadott jövedelmi és egyéb adatok szerint
									tartalmazza a legolcsóbb hitelajánlatokat. A kalkulátor a legkedvezőbb ajánlat
									felkutatása után sem hagy magadra, lehetőséged van egyúttal tájékozódni az egyes
									banki termékek feltételeiről és egyúttal online elindítani az igénylést.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Ne feledkezz meg a kedvezmények feltételeiről!">
								<p>
									Érdemes a legolcsóbb személyi hitelt választanod, egy jó döntéssel akár több
									százezer forintot is spórolhatsz. Ugyanakkor nem szabad megfeledkezned arról sem,
									hogy{" "}
									<strong>
										milyen feltételeket kell az adott kedvezményes kamat eléréséért teljesítened.
									</strong>
								</p>
								<p>
									Különösen azok a feltételek lehetnek lényegesek számodra, melyeket az egész futamidő
									alatt elvárna a bank tőled. (Például, hogy egy meghatározott jóváírás érkezzen
									havonta a számládra, hitelfedezeti életbiztosítás fenntartása a futamidő alatt...){" "}
								</p>
								<p>
									Át kell gondolnod, hogy ezen feltételek közül melyeket tudod nagy biztonsággal
									teljesíteni a kölcsön lejáratáig. Az elvárás nem teljesítése miatt ugyanis
									elveszítheted a kamatkedvezményt, kölcsönöd kamata pedig jelentősen megemelkedhet.{" "}
								</p>
								<p>
									Előfordulhat, hogy számodra jobban megérné egy kicsivel drágább hitel abban az
									esetben, ha az ahhoz kapcsolódó akciós feltételeket végig, biztosan teljesíteni
									tudod.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Ne csak a legolcsóbb ajánlattal foglalkozz a kalkulációt követően!">
								<p>
									Természetesen senki sem szeret feleslegesen fizetni, ettől függetlenül érdemes
									megismerkedni a legolcsóbb ajánlat mellett legalább még egy-két pénzintézet
									kölcsönével is.
								</p>
								<p>
									Ismerd meg az ottani feltételeket, elvárásokat, lehetőségeket is. Ha a legolcsóbb
									ajánlat valami miatt nem lenne megfelelő, akkor tudod milyen további lehetőségeid
									vannak.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Ismerd meg az egyes bankok egyedi bírálati elvárásait is!">
								<p>
									Az egyes banki ajánlatok nem csak a kamatban és egyéb kondíciókban térhetnek el
									egymástól. Jelentős{" "}
									<strong>különbségek lehetnek, a bírálati szabályok, elvek között is.</strong>{" "}
									Például eltérő módon, mértékben fogadják el a speciális jövedelmeket.{" "}
								</p>
								<p>
									Ezek a tényezők azonban nem derülnek ki feltétlenül egy nyilvános banki
									dokumentumból, éppen ezért érdemes lehet független hitelszakértőhöz fordulni, aki
									segít eligazodni a feltételek útvesztőjében. A Bankmonitor szakértői naprakész
									információkkal rendelkeznek a banki hitelbírálati szabályokról, ráadásul díjmentesen
									a rendelkezésedre állnak. Így neked már nem szükséges személyesen több banknál is
									érdeklődni, vagy online végigböngészni az összes banki hirdetményt.
									Hiteltanácsadóink eleve ahhoz a pénzintézethez irányítanak, ahol a legvalószínűbb
									számodra a sikeres igénylés.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Nem kell kétségbe esni, ha nem kaphatod meg a kért hitelösszeget!">
								<p>
									Előfordulhat, hogy a kiválasztott bank csak kevesebb kölcsönt hagyna jóvá, ne adj
									Isten elutasítaná a kérelmedet. Ebben az esetben sem szabad kétségbe esni, a
									független hitelszakértővel karöltve át kell gondolni a lehetőségeidet:
								</p>
								<ul>
									<li>
										Ha a jövedelmed nem volt elégséges – jellemzően ez szokott lenni a probléma
										személyi hitel esetében -, akkor adóstárs bevonásával növelheted az igényelhető
										összeg nagyságát. Ebben az esetben ugyanis a pénzintézet a kettőtök együttes
										jövedelme alapján határozza meg a bevállalható törlesztőrészlet nagyságát.
									</li>
									<li>
										Érdemes más pénzintézetnél is érdeklődni. (Ez különösen akkor igaz, ha
										valamilyen speciális jövedelmet igazoltál le.) Ugyanis más pénzintézetek akár
										jóval magasabb hitelösszeget is jóváhagyhatnak számodra.
									</li>
									<li>
										Az is előfordulhat, hogy csupán egy rövid időt kell várnod az igényléssel. (Nem
										elegendő a munkaviszonyod, vagy nem rendelkezel még megfelelő vállalkozói
										múlttal.)
									</li>
									<li>
										Érdemes tehát ebben az esetben is egy bankfüggetlen hitelszakértőhöz fordulnod,
										aki a banki gyakorlatok ismeretében segíthet a továbbiakban.
									</li>
								</ul>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Tartsd észben, hogy más finanszírozási lehetőségek is vannak">
								<p>
									Ne feledd, hogy a fedezetlen hitelek mellett más finanszírozási lehetőség is
									elérhető lehet számodra. Például lakáshitel,{" "}
									<a href="/lakashitel-kalkulator/szabad-felhasznalasu-jelzaloghitel-kalkulator/?link_type=landing-faq">
										szabad felhasználású jelzáloghitel
									</a>
									, vagy <a href="/babavaro-hitel/?link_type=landing-faq">Babaváró hitel</a> is
									elérhető a piacon. A te saját élethelyzeteden, céljaidon is múlik, hogy számodra
									melyik a legjobb megoldás. Ebben is segítségedre lesz a Bankmonitor
									hitelügyintézője.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Hogyan juthatsz gyorsan kölcsönhöz?">
								<p>Előfordulhat, hogy az idő fontosabb számodra a költségeknél:</p>
								<ul>
									<li>A felújítás közepén elfogyott a pénzed.</li>
									<li>Határidőre ki kell fizetned egy nagyobb összeget.</li>
									<li>Lerobbant az autód, ami nélkül nem tudsz dolgozni.</li>
									<li>Elromlott egy nélkülözhetetlen háztartási berendezés…</li>
								</ul>
								<p>
									Ebben a helyzetben a legfontosabb, hogy a problémát minél előbb orvosold. Ha
									kölcsönre lenne szükséged, akkor pedig nincs időd 4-6 hetet várni: átlagosan ennyi
									idő alatt bírálnak el egy szabad felhasználású jelzáloghitelt a bankok.
								</p>
								<p>
									Egy személyi kölcsön választásával az igénylés benyújtását követő egy héten belül
									megkaphatod a kért hitelösszeget. Tovább gyorsíthatod a folyamatot, ha a kalkuláció
									és az érdeklődés leadása után kitöltöd a Bankmonitor által számodra elküldött
									adatbekérőt, így kollégáinkkal jellemzően már nincs szükség hosszas egyeztetésre,
									rövidesen konkrét ajánlattal kereshetnek meg és elküldik számodra a legkedvezőbb
									banki ajánlatokat a választott hitelösszegre.
								</p>
								<h4>Válaszd a Minősített Fogyasztóbarát Személyi Hitelt!</h4>
								<p>
									A fogyasztóbarát személyi kölcsön választásával még gyorsabban megkaphatod a
									hitelösszeget. A kölcsönt ugyanis a befogadást követő naptól számított három
									munkanapon belül ki kell folyósítani. Sőt ez a határidő online személyi kölcsön
									igénylés esetén lerövidül két munkanapra.
								</p>
								<h4>Időben kezd el az igényléshez szükséges dokumentumok begyűjtését!</h4>
								<p>
									A személyi hitel igénylésekkel kapcsolatban a bankok gyorsan döntést hoznak,
									várhatóan egy hét alatt a számládon lehet a pénz a kérelem beadását követően. Ennek
									azonban feltétele, hogy minden dokumentum rendelkezésre álljon időben. Éppen ezért
									érdemes átgondolni azt, hogy mire is lesz szükséged a kölcsön igényléséhez és a
									megfelelő igazolások beszerzéséről időben gondoskodni kell.
								</p>
								<p>Milyen dokumentumokra lehet szükséged személyi kölcsön igénylésekor?</p>
								<ul>
									<li>
										személyes okmányok (személyi igazolvány/ jogosítvány/ útlevél és lakcímkártya)
									</li>
									<li>legalább 2-3 havi bankszámlakivonat,</li>
									<li>jövedelemigazoláshoz szükséges dokumentumok,</li>
									<ul>
										<li>munkáltatói igazolás</li>
										<li>NAV jövedelemigazolás (speciális esetben)</li>
									</ul>
									<li>magyarországi telefonos elérhetőség.</li>
								</ul>
							</LandingAccordion>
						</Accordion>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderBmHelp(): ReactNode {
		return (
			<LandingContainer id="miben-segithet-a-bankmonitor" sendEvent>
				<Row className="justify-content-center">
					<Col md={10}>
						<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Miben segít a Bankmonitor?</h2>
						<Accordion>
							<LandingAccordion title="Találd meg a legkedvezőbb személyi kölcsönt a Bankmonitor hitelkalkulátorával ">
								<p>
									A Bankmonitor személyi kölcsön kalkulátorának segítségével összehasonlíthatod a
									banki kölcsönöket. Ráadásul a megadott adatok alapján személyre szabott ajánlatokat
									láthatsz a találati listában, illetve a részletes kereső használatával a hitelek
									összehasonlítása még pontosabb képet ad a lehetőségekről.
								</p>
								<p>
									A Bankmonitor kalkulátora a hitelpiac 95 százalékát lefedi, az alábbi pénzintézetek
									ajánlataival találkozhatsz, ha minket választanál:
								</p>
								<ul>
									<li>CIB Bank </li>
									<li>Erste Bank </li>
									<li>OTP Bank</li>
									<li>Raiffeisen Bank</li>
									<li>K&H Bank</li>
									<li>UniCredit Bank</li>
									<li>MBH Bank</li>
									<li>Cetelem Bank</li>
								</ul>
								<p>
									Sőt, a{" "}
									<a href="/mennyi-szemelyi-kolcsont-kaphatok/?link_type=landing-faq">
										Mennyi hitelt kaphatok kalkulátor
									</a>{" "}
									segítségével azt is megtudhatod, hogy a jövedelmed alapján mennyi hitel felvételére
									lehetsz jogosult az egyes pénzintézeteknél. Ha számodra az igényelt összeg fontos,
									akkor érdemes ezzel a lépéssel kezdened.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Tudj meg mindent a Bankmonitor oldalát olvasva a személyi kölcsönről">
								<p>
									Természetesen a speciális részleteket, egyedi elvárásokat nem tudja minden esetben a
									kalkulátor visszaadni. Éppen ezért érdemes lehet a Bankmonitor oldalán található
									tájékoztatókat, leírásokat is elolvasni. Így megismerheted az általános elvárásokat
									és sok esetben az egyes bankok speciális feltételeit is. Használd az oldalon
									található keresőt a találatok pontosításához!
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="A Bankmonitor hitelügyintézői ismerik a bankok közötti különbségeket">
								<p>
									Érdemes a kalkulációt követően a hiteligénylést is a Bankmonitoron keresztül, online
									elindítanod. Szakértő kollégáink ismerik az összes bank egyedi eljárását, az apró
									részleteket is. Segítenek kiválasztani azt a pénzintézetet, amelyik a legnagyobb
									eséllyel adja meg neked a kért hitelösszeget.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Segítünk a dokumentumok összegyűjtésében, az igénylés benyújtásában">
								<p>
									Nem csak az egyedi bírálati elvekkel, hanem az igényléshez szükséges dokumentumokkal
									is tisztában vannak a Bankmonitor szakértői. Segítenek a szükséges papírok
									összegyűjtésében és magában az igénylésben is. Ezzel annak esélye is jelentős
									csökkeni fog, hogy valami lényeges papír lemarad: ezáltal a bírálati idő
									lerövidülhet, a pénz kifizetése is gyorsabban megtörténhet.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Kedvezőbb feltételekkel kaphatod meg a kölcsönt a Bankmonitoron keresztül">
								<p>
									Akár anyagi előnye is lehet annak, hogy a Bankmonitort választod. Egyes esetekben
									ugyanis ügyfeleik számára speciális díjkedvezményeket biztosítanak a bankok abban az
									esetben, ha a Bankmonitoron keresztül igénylik a hitelt. (Ez lehet akár egy
									kamatkedvezmény, vagy épp az előtörlesztési díj mérséklése is.) A személyi kölcsön
									kalkulátorban az egyes ajánlatok mellett tájékozódhatsz arról is, hogy milyen egyedi
									kedvezményeket zsebelhetsz be, ha a Bankmonitoron keresztül, online indítod el az
									igénylést, illetve arról is olvashatsz, hogy mik az egyes akciók feltételei.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Szolgáltatásunk számodra teljesen ingyenes">
								<p>
									Egyáltalán nem lényegtelen az sem, hogy szolgáltatásunk teljesen ingyenes számodra.
									A Bankmonitor bevétele a bankoktól származik, hiszen megkönnyítjük az ő munkájukat
									is: számos adminisztrációs terhet levéve a banki ügyintézők válláról.
								</p>
							</LandingAccordion>
						</Accordion>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderFogalomTar(): ReactNode {
		return (
			<LandingContainer id="fogalomtar" sendEvent>
				<Row className="justify-content-center">
					<Col md={10}>
						<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Fogalomtár</h2>
						<Accordion>
							<LandingAccordion title="Személyi kölcsön">
								<p>
									A személyi kölcsön egy ingatlanfedezet nélküli hitel. Az igényelhető hitelösszeget
									szinte kizárólag a jövedelmed nagysága befolyásolja. A hitel jellemzően szabad
									felhasználású, de léteznek speciális célra felhasználható kölcsöntípusok is,
									amelyekről fentebb részletesen írtunk.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Ingatlanfedezet">
								<p>
									Ingatlanfedezetről akkor beszélhetünk, ha a kölcsön mögé felajánlod a lakásodat is.
									Ezen ingatlanra a bank jelzálogjogot jegyeztet be, ez lenne a kölcsön biztosítéka
									is.{" "}
								</p>
								<p>
									Ingatlanfedezet nélküli hitelek esetében a kölcsönösszeg mögött nincsen
									ingatlanfedezet, a bank nem terheli meg jelzálogjoggal az ingatlant. A személyi
									hitel igénylésekor nincsen szükség ingatlanfedezet felajánlására, így azok is
									élhetnek a gyorsan igényelhető kölcsönnel, akiknek nem áll rendelkezésre saját
									ingatlan, vagy azon már teher van.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Futamidő">
								<p>
									A futamidő az az időszak, amely alatt a kölcsönt vissza kell fizetned. (A
									visszafizetés jellemzően havonta egyenlő részletekben történik.) A futamidő
									növelésével csökken a havi törlesztőrészlet, ugyanakkor a teljes visszafizetendő
									összeg megemelkedik. Ez érthető, hiszen tovább használod a bank pénzét.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="THM">
								<p>
									A THM (teljes hiteldíj mutató) egy kölcsön éves átlagos költségét fejezi ki
									százalékos formában. A rendszeresen megfizetendő törlesztőrészletek mellett az eseti
									díjak, költségek is megjelennek a mutató értékében. A THM az egyes banki ajánlatok
									költségének összehasonlítására használható.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Hitelkamat">
								<p>
									A hitelkamat a futamidő alatt a kölcsönösszegre megfizetendő százalékos díj. Ezt
									tekinthetjük a hitel árának is. A kamatot a havi törlesztőrészlettel egyidőben
									szükséges rendezni.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Kamatperiódus">
								<p>
									Az az időszak, amelyen belül a bank egyoldalúan nem módosíthatja a kamatot, vagyis a
									törlesztőrészlet változatlan. Személyi kölcsönt jelenleg jellemzően fix kamat
									mellett lehet igényelni, vagyis a kamat mértéke a futamidő alatt nem módosulhat.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Törlesztőrészlet">
								<p>
									A hitelre havonta fizetendő összeg amely két részből tevődik össze: a tőkerészből és
									a kamatból. A hitelek többségét jellemzően egyenlő részletekben szükséges
									megfizetni, ez azt jelenti, hogy minden hónapban azonos mértékű a törlesztőrészlet.
									(Legalábbis addig, amíg a kamat nem változik.)
								</p>
								<p>
									A futamidő előrehaladtával a törlesztőrészlet kamatrésze egyre csökken, míg a
									tőkerész növekszik.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Adóstárs">
								<p>
									A hitelügyletbe bekerülhet az adós mellé más szereplő is, őt adóstársnak, vagy
									társigénylőnek nevezhetjük. Ezen személynek is teljesítenie kell a bank alapvető
									elvárásait. A kérdéses személy is felelősséget vállal a hitel visszafizetésére: ha
									az adós nem rendezi a törlesztőrészletet, akkor tőle is követelheti a pénzintézet az
									elmaradt összeget.
								</p>
								<p>
									Az adóstárs bevonása esetén az ő jövedelmét is figyelembe veszik a bevállalható
									törlesztőrészlet meghatározásakor, vagyis emelkedhet az igényelhető hitelösszeg
									nagysága az adóstárs vagy társigénylő bevonásával.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Fogyasztóbarát személyi kölcsön">
								<p>
									A fogyasztóbarát minősítést a jegybank hozta létre. A különleges pályázati
									feltételeknek megfelelő banki kölcsönök elnyerhetik a jegybanktól a fogyasztóbarát
									besorolást. Ez egyfajta jelzés lehet feléd is, hiszen a kölcsön az alap elvárásoknál
									szigorúbb, egységes feltételeknek is megfelel.
								</p>
								<p>A fogyasztóbarát minősítés elnyerésének főbb feltételei személyi hitelek esetén:</p>
								<ul>
									<li>
										Kamatplafon került kialakításra. (A hitel kamata 500 ezer forintot meghaladó
										hitelösszeg esetén a referenciakamatot legfeljebb 10 százalékponttal, az ennél
										kisebb hitelösszeg esetén pedig 15 százalékponttal haladhatja meg.)
									</li>
									<li>A kamat a futamidő végéig fix kell legyen.</li>
									<li>A futamidő legfeljebb 7 év lehet.</li>
									<li>
										A folyósítási díj legfeljebb a hitelösszeg 0,75 százaléka lehet. (Online
										igénylés esetén nem számítható fel folyósítási díj.)
									</li>
									<li>Teljes online igénylési folyamatot szükséges biztosítani.</li>
									<li>
										A befogadástól a folyósításig – pozitív bírálat esetén – legfeljebb 3 munkanap
										telhet el. (Online igénylés esetén ez a határidő csupán 2 munkanap.)
									</li>
									<li>
										Az előzetes tájékoztatás során a banknak át kell adnia a hitelfolyamat során
										szükséges összes dokumentumot tartalmazó ellenőrzőlistát.
									</li>
									<li>
										A kérelem beadását követően kötelező ajánlatot kap a banktól az ügyfél, és az
										abban foglalt feltételeknek megfelelően kell megkötniük a szerződést. (Az
										ajánlatnak legalább 3 munkanap az érvényességi ideje.)
									</li>
								</ul>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Személyi kölcsön online">
								<p>
									A digitális technológia folyamatos fejlődésének hála ma már az otthon kényelméből,
									akár teljesen online folyamattal is végigjárhatjuk a kölcsön igénylésének lépéseit.
									Ma már több banknál és pénzügyi tanácsadó cégen keresztül lehetőségünk van akár
									személyes jelenlét nélkül, videós azonosítással személyi hitelt igényelni. A
									Bankmonitor személyi kölcsön kalkulátorán keresztül lehetőséged van online
									elindítani az igénylési folyamatot, ezt követően szakértő kollégáink segítenek végig
									a szükséges lépéseken. Az egyes banki folyamatoktól függően az ügyfél részéről elég
									lehet csak egyetlen bankfióki látogatás, amikor a tényleges szerződéskötés történik.
									Fontos tudni, hogy a minősített fogyasztóbarát személyi hitelek elterjedésével a
									folyamat teljesen online is igénybe vehető és akár napokkal rövidebb lehet.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Adósságrendező személyi kölcsön">
								<p>
									Adósságrendezésről akkor beszélhetünk, ha több korábbi fedezetlen hitelünket
									szeretnénk rendezni, mégpedig egyetlen új személyi hitel igénylésével. Ezzel szemben
									hitelkiváltáskor egyetlen meglévő tartozást cserélünk le egy új hitellel. Az
									adósságrendező személyi kölcsön tulajdonképpen egy olyan speciális személyi hitel,
									amely kizárólag a tartozás(ok) rendezése céljából vehető fel.{" "}
								</p>
								<p>
									Elsődleges, hogy ezáltal kedvezőbb anyagi helyzetbe kerüljünk, emellett az új
									kölcsön törlesztése sokkal átláthatóbb, kiszámíthatóbb lesz az adós számára. Egyéni
									élethelyzettől függően az adósságrendező személyi kölcsön segíthet a futamidő
									rövidítésében is, ezáltal hamarabb szabadulhatunk a terhek alól.{" "}
								</p>
								<p>
									Adósságrendező személyi kölcsönnel jellemzően kisebb összegű fedezetlen kölcsönt
									vagy kölcsönöket lehet egyszerre kiváltani, például személyi kölcsönt, egyéb
									fogyasztási hiteleket, folyószámlahitel tartozást, áruhitelt, hitelkártya tartozást.
								</p>
								<p>
									<a href="/adossagrendezo-hitel/?link_type=landing-faq">
										Nézd meg, melyik most a legolcsóbb személyi kölcsön hiteleid kiváltására
									</a>{" "}
									az Adósságrendező hitel kalkulátorral!
								</p>
								<p>
									Ha pedig{" "}
									<a href="https://bankmonitor.hu/lakashitel-hitelkivaltas/?link_type=landing-faq">
										jelzáloghitellel
									</a>{" "}
									is rendelkezel, akkor a hitelkiváltás kalkulátor lehet számodra érdekes.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Gyorskölcsön">
								<p>
									A gyorskölcsönök jellemzően kisebb összegű hitelek, amik kifejezetten gyorsan, akár
									24 órán belül felvehetők, rövid futamidőre igényelhetők és gyakran heti törlesztés
									jellemzi őket, fix törlesztőrészletekkel. A gyorskölcsön nem azonos a személyi
									kölcsönnel, ami nagyobb hitelösszeg igénylését is lehetővé teszi, ugyanakkor a
									törlesztés havonta történik és hosszabb futamidőre is igényelhető. Fontos tudni,
									hogy a gyorsaságnak mindig ára van, így a gyorskölcsönök jellemzően jóval drágábbak
									a személyi kölcsönöknél.
								</p>
							</LandingAccordion>
						</Accordion>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderFaq(): ReactNode {
		return (
			<LandingContainer id="gyakori-kerdesek" sendEvent>
				<Row className="justify-content-center">
					<Col md="10">
						<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Gyakori kérdések</h2>
						<Accordion>
							<LandingAccordion title="Kifizethetem idő előtt a hitelem fennálló tartozását?">
								<p>
									A személyi kölcsön a futamidő lejárta előtt{" "}
									<a href="/jobb-tudni/szemelyi-kolcson/?link_type=landing-faq#vegtorlesztes-elotorlesztes">
										elő- vagy végtörleszthető
									</a>
									. Ennek a költsége (előtörlesztési díj) jellemzően a fennálló tartozás 1 százaléka,
									de ha egy évnél rövidebb idő van már csak hátra a futamidőből, akkor 0-0,5 százalék
									közötti ennek a költsége.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Részmunkaidős munkavállalóként megkaphatom a hitelt?">
								<p>
									Akár részmunkaidős munkaviszonnyal is igényelhető személyi kölcsön, amennyiben az
									igazolható nettó jövedelem összege eléri a szükséges mértéket. A bankok többségénél
									az elvárt legalacsonyabb jövedelem megegyezik az aktuális minimálbér összegével. De
									arról sem szabad megfeledkezni, hogy a jövedelemnek el kell bírnia a
									törlesztőrészlet nagyságát, amit az úgynevezett{" "}
									<a href="/jobb-tudni/babavaro/#jovedelemaranyos-torlesztoreszlet-mutato-jtm/?link_type=landing-faq">
										JTM szabály
									</a>{" "}
									ír elő.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Mindenképp a hitelt nyújtó bankhoz kell utaltatni a jövedelmemet?">
								<p>
									A személyi kölcsönnek nem feltétele a banknál vezetett bankszámlára történő
									jövedelemérkeztetés, ám ezt a legtöbb pénzintézet kamatkedvezménnyel jutalmazza.
									Előfordulhat ugyanakkor, hogy az igénylőnek van már lakáshitele, amelynél ugyancsak
									kamatkedvezményt ér a jövedelemérkeztetés, így ilyenkor mérlegelni kell, mi jelent
									nagyobb pénzügyi előnyt.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Készpénzben kapom a jövedelmem, kaphatok személyi hitelt?">
								<p>
									Nem minden pénzintézet fogadja el a készpénzben kapott jövedelmet. Ahol megfelelő,
									ott is NAV jövedelemigazolás bemutatását várja el a bank, amelyből láthatja, hogy az
									igénylőnek lesz miből fizetnie a törlesztőket. Ezen kívül kell még egy munkáltatói
									igazolás, ami mutatja, hogy van legalább 3 hónap munkaviszony, illetve letelt a 3-6
									hónap próbaidő.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Elfogadható lakásvásárlásnál önerőként a személyi hitel?">
								<p>
									Az <a href="https://www.mnb.hu/web/fooldal">MNB</a> (Magyar Nemzeti Bank)
									iránymutatása szerint ez a hitel típus nem használható fel önerőként lakáshitelhez,
									ugyanis ez növelné a lakáshitelezés kockázatát. Ezért a lakáshitellel párhuzamosan,
									vagy az azt megelőző 3 hónapon belül igényelt fedezetlen hitelek felhasználását
									ellenőriznie kell a bankoknak. Legalábbis azt, hogy ezen összegek nem képezik-e
									részét a lakásvásárlás önerejének. Amennyiben a vásárlásra szánt saját megtakarítás
									kiegészítésére használná az igénylő a személyi hitelt, akkor meghiúsulhat a
									szerződéskötés.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Szükséges személyi kölcsönhöz ingatlanfedezet?">
								<p>
									Ahogy említettük, nem szükséges ingatlanbiztosíték a kölcsön mögé. A jelzálogjoggal
									biztosított személyi hitelt{" "}
									<a href="/lakashitel-kalkulator/szabad-felhasznalasu-jelzaloghitel-kalkulator/?link_type=landing-faq">
										szabad felhasználású jelzáloghitelnek
									</a>{" "}
									nevezik és az egy teljesen más hitelkonstrukció.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Személyi kölcsön vagy lízing?">
								<p>
									Ha{" "}
									<a href="/mediatar/video/autohitel-vagy-autolizing-kerulj-jobb-alkupozicioba-autovasarlaskor/?link_type=landing-faq">
										autóhitelről
									</a>{" "}
									hallasz, az tulajdonképpen egy szabad felhasználásra igényelhető személyi kölcsönt
									takar amelyet autóvásárlásra is fordíthatsz. Gépjárművásárlás előtt gyakori kérdés,
									hogy a személyi hitel, vagy a pénzügyi lízing a jobb finanszírozási forma. A lízing
									esetében minimálisan 20 százalék önerő szükséges, míg a személyi kölcsönnél nincs
									önerőelvárás. Viszont autólízing esetében a maximális futamidő és hitelösszeg
									jellemzően magasabb. Amennyiben valaki közvetlenül hozna be külföldről autót, akkor
									kizárólag személyi hitelek (autóhitelek) jöhetnek szóba. Érdemes hozzátenni, hogy a
									lízingnél új autóvásárlás esetében elérhető lehet gyári finanszírozás is, amelynél
									akár 2-4 százalékos, bizonyos esetekben akár 0 százalékos THM is elérhető. Ez így
									gyakorlatilag olyan, mint egy 0 százalékos THM-mel elérhető áruhitel.
								</p>
							</LandingAccordion>
						</Accordion>
						<Accordion>
							<LandingAccordion title="Mi történik, ha nem tudom fizetni a törlesztőt?">
								<p>
									Alapvetően úgy kell megtervezni egy hitelfelvételt, hogy jövedelemkiesés esetén is
									stabilan lehessen fizetni. Azonban előfordulhat, hogy a törlesztés mégis nehezebben
									megy. Azt nagyon fontos szem előtt tartani, hogy késedelmes fizetés esetén
									büntetőkamatot kell fizetni, de drágán számolják a felszólító levél díját is. A
									lehető leggyorsabban fel kell keresni a bankot, jelezni feléjük a nehézséget és a
									bankkal közösen megoldást keresni.{" "}
									<a href="/vedelem/bankmonitor-biztonsag/?link_type=landing-faq">
										Hitelfedezeti biztosítással
									</a>{" "}
									ezeket a nehéz helyzeteket meg lehet előzni.
								</p>
							</LandingAccordion>
						</Accordion>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderUsefulLinks(): ReactNode {
		return (
			<LandingContainer>
				<Row className="justify-content-center">
					<Col md="10">
						<h2 id="links">Hasznos linkek személyi hitel felvétele előtt</h2>

						<p>Mielőtt személyi kölcsönt vennél fel, javasoljuk, hogy olvasd el az alábbi tájékoztatókat</p>
						<ul>
							<li>
								<a
									href="https://www.mnb.hu/fogyasztovedelem/hitel-lizing/hitelfelvetel/mirol-kell-tajekoztatnia-a-hitelugyintezonek"
									target="_blank"
									rel="noreferrer"
								>
									Miről kell tájékoztatnia a hitelügyintézőnek?
								</a>
							</li>
							<li>
								<a
									href="https://www.mnb.hu/fogyasztovedelem/hitel-lizing/hitelfelvetel/hasznos-tanacsok-hitelfelvetel-elott"
									target="_blank"
									rel="noreferrer"
								>
									Hasznos tanácsok hitelfelvétel előtt
								</a>
							</li>
							<li>
								<a
									href="https://www.mnb.hu/mszh/intezmenyeknek#palyazati-eredmenyek"
									target="_blank"
									rel="noreferrer"
								>
									Minősített fogyasztóbarát személyi hitel termékeket forgalmazó pénzügyi intézmények
									listája
								</a>
							</li>
							<li>
								<a href="https://www.mnb.hu/mszh" target="_blank" rel="noreferrer">
									Milyen személyi hitelek nyerhetik el az MNB fogyasztóbarát minősítését
								</a>
							</li>
							<li>
								<a
									href="https://www.mnb.hu/letoltes/fogyasztasi-hitelek.pdf"
									target="_blank"
									rel="noreferrer"
								>
									A Pénzügyi Navigátor tájékoztatója a fogyasztási hitelekről
								</a>
							</li>
							<li>
								<a
									href="https://www.mnb.hu/fogyasztovedelem/hitel-lizing/fogyasztasi-hitelek/szemelyi-kolcson"
									target="_blank"
									rel="noreferrer"
								>
									Általános tudnivalók a személyi kölcsönről
								</a>
							</li>
							<li>
								<a
									href="https://www.mnb.hu/fogyasztovedelem/hitel-lizing/fogyasztasi-hitelek/folyoszamlahitel"
									target="_blank"
									rel="noreferrer"
								>
									Folyószámla hitel
								</a>
							</li>
							<li>
								<a
									href="https://www.mnb.hu/fogyasztovedelem/hitel-lizing/fogyasztasi-hitelek/a-hitelkartyarol"
									target="_blank"
									rel="noreferrer"
								>
									Hitelkártya
								</a>
							</li>
							<li>
								<a
									href="https://www.mnb.hu/fogyasztovedelem/hitel-lizing/fogyasztasi-hitelek/aruhitelek"
									target="_blank"
									rel="noreferrer"
								>
									Áruhitelek
								</a>
							</li>
						</ul>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderVideos(): ReactNode {
		return (
			<LandingContainer as="aside">
				<div className="headline">
					<h2 className="h6 text-uppercase">Kapcsolódó videók</h2>
				</div>
				<Row className="justify-content-center">
					<Col lg={3} md={6} className="mt-3 mt-lg-0">
						<YoutubeVideo
							title="Személyi kölcsön kalkulátor 2023 lépésről lépésre - Készíts összehasonlítást pillanatok alatt"
							videoId="YdnLYpaqyss"
							description="Aki egy kicsit is tudatosan intézi a pénzügyeit tudja, hogy egy hiteligénylésnél az aktuális banki ajánlatok összehasonlítása kulcsfontosságú. Azonban nem csak ebben lesz segítségedre Bankmonitor innovatív személyi kölcsön kalkulátora. Videónkban lépésről lépésre bemutatjuk hogyan használd, hogy személyre szabott személyi hitel összehasonlítást kapj és azt is megtudhatod, hogyan veheted fel velünk villámgyorsan a kapcsolatot a kalkulátor segítségével. Emellett Dékány Balázs, a Bankmonitor pénzügyi szakértője számos tippet és trükköt is mutat, például, hogy hogyan gyorsíthatod meg az igénylési folyamatot pár kattintással."
							uploadDate="2023-07-10"
						/>
					</Col>
					<Col lg={3} md={6} className="mt-3 mt-lg-0">
						<YoutubeVideo
							title="Személyi kölcsön igénylés - Minden, amit tudnod kell 2023-ban"
							videoId="Xo362VO1AEU"
							description="Nem szükséges hozzá ingatlan fedezet, elegendő „csupán” igazolt jövedelmet felmutatni a bankoknak, ha személyi kölcsönt szeretnénk. Tényleg ilyen egyszerű lenne ez a történet? Mi és hogyan működik a valóságban? Hogyan bírálják el a személyi kölcsön igényeinket a bankok? Interjú a Bankmonitor vezető pénzügyi szakértőjével, Dékány Balázzsal a legfontosabb tudnivalókról. Forintra váltható tudás érthetően."
							uploadDate="2021-11-04"
						/>
					</Col>
					<Col lg={3} md={6} className="mt-3 mt-lg-0">
						<YoutubeVideo
							title="Személyi kölcsön - érdemes most előtörleszteni?"
							videoId="1-KswE4S1Bs"
							description="Egy érdekes felhasználói kérdésre lettünk figyelmesek Reddit-en, miszerint ha több személyi kölcsönnel rendelkezünk, érdemes-e most, az aktuális környezetben havonta kisebb összegekkel előtörleszteni? Argyelán József, a Bankmonitor vezető elemzője a kérdező konkrét példáján keresztül mutatja be a lehetőségeket: Érdemes-e előtörleszteni vagy van jobb helye is a pénznek? Mekkora díja van, lehet-e ingyenes az előtörlesztés? Hol állnak jelenleg a személyi hitel költségek? Mi a helyzet a befektetések lehetőségével és hozamával? Ki segíthet, ha konkrét lépésekre kerülne sor?"
							uploadDate="2023-05-05"
						/>
					</Col>
					<Col lg={3} md={6} className="mt-3 mt-lg-0">
						<YoutubeVideo
							title="Személyi kölcsön igénylés - Minden, amit tudnod kell 2023-ban"
							videoId="mTI3oJWBv28"
							description="Nem szükséges hozzá ingatlan fedezet, elegendő „csupán” igazolt jövedelmet felmutatni a bankoknak, ha személyi kölcsönt szeretnénk. Tényleg ilyen egyszerű lenne ez a történet? Mi és hogyan működik a valóságban? Hogyan bírálják el a személyi kölcsön igényeinket a bankok? Interjú a Bankmonitor vezető pénzügyi szakértőjével, Dékány Balázzsal a legfontosabb tudnivalókról. Forintra váltható tudás érthetően."
							uploadDate="2021-11-04"
						/>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderCards(): ReactNode {
		const colProps = {
			className: "my-3 align-items-stretch",
			lg: 3,
			md: 6,
			sm: 6,
		};

		return (
			<Row>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Mennyi kölcsönt <br className="d-sm-block d-none" />
								kaphatok?
							</>
						}
						icon={<FontAwesomeIcon icon={farQuestion} />}
						href="/mennyi-szemelyi-kolcsont-kaphatok/"
						buttonText="Kalkuláció indítása"
					>
						Tudd meg jövedelmed alapján, maximálisan mekkora összegű szabad felhasználású, fix kamatú
						személyi kölcsönt kaphatsz a magyarországi bankoktól.
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Prémium személyi
								<br className="d-sm-block d-none" />
								kölcsön
							</>
						}
						icon={<FontAwesomeIcon icon={farMoneyBillWave} />}
						href="/szemelyi-kolcson-premium/"
						buttonText="Kalkuláció indítása"
					>
						Végig fix kamatú személyi kölcsönök, villámgyors folyósítással. Szabadfelhasználásra,
						autóvásárlásra vagy akár lakásfelújításra.
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Autóhitel <br className="d-sm-block d-none" />
								kalkulátor
							</>
						}
						icon={<FontAwesomeIcon icon={farCar} />}
						href="/autohitel/"
						buttonText="Kalkuláció indítása"
					>
						Fix kamatú személyi kölcsönök használt és új autó vásárlására, fedezet nélkül, gyors
						hitelbírálattal és folyósítással.
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Felújítási <br className="d-sm-block d-none" />
								hitel
							</>
						}
						icon={<FontAwesomeIcon icon={farPaintRoller} />}
						href="/felujitasi-hitel/"
						buttonText="Kalkuláció indítása"
					>
						A megfelelő személyi kölcsönt keresed felújításodhoz? Kalkulálj és spórolj akár százezreket egy
						jó döntéssel!
					</FeatureBox>
				</Col>
			</Row>
		);
	}

	private getSchema(): FAQSchema {
		return {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: [
				{
					"@type": "Question",
					name: "Melyek a személyi kölcsön legfontosabb feltételei?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A személyi kölcsön igényléséhez megfelelő nagyságú igazolt jövedelemmel kell rendelkeznünk, legalább három hónapja az adott munkahelyen kell dolgoznunk, valamint nem állhatunk már próbaidő alatt.",
					},
				},
				{
					"@type": "Question",
					name: "Mennyi személyi kölcsönt kaphatok?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Személyi kölcsön esetében általában legfeljebb 10 millió forint vehető fel, de sok bank 6-8 millió forintnál húzta meg az elérhető maximális összeget.",
					},
				},
				{
					"@type": "Question",
					name: `Milyen célokra igényelhető személyi hitel {currentYear(true)}?`,
					acceptedAnswer: {
						"@type": "Answer",
						text: "A személyi kölcsön alapvetően egy szabad felhasználású hitel, így az adós bármire költheti a felvett összeget. Ugyanakkor a bankok kínálnak konkrét hitelcélhoz kapcsolódó személyi kölcsönöket is, így igényelhető például lakásfelújításra, vagy autóvásárlásra is.",
					},
				},
				{
					"@type": "Question",
					name: "Mennyi idő alatt kapjuk meg a személyi kölcsönt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Amennyiben minden szükséges dokumentumot benyújtottunk a bank részére (jövedelemigazolás, munkáltatói igazolás, legalább 3 havi bankszámlakivonat), a hiteligénylésünket pedig pozitívan bírálta el a bank, akkor általában egy héten belül megérkezhet számlánkra a pénzt.",
					},
				},
				{
					"@type": "Question",
					name: "Melyik a legbiztonságosabb személyi kölcsön?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A kamatkockázat a személyi kölcsönök esetében is jelen van, ezért érdemes végéig fix kamatú hitelt választani, ahol a havi törlesztő a futamidő során nem változhat.",
					},
				},
				{
					"@type": "Question",
					name: "Kaphatok személyi kölcsönt, ha készpénzben kapom a jövedelmem?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Készpénzes jövedelem esetén NAV jövedelemigazolásra lesz szükség, amelyen a bank láthatja, hogy az igénylőnek lesz miből fizetnie a törlesztőket. Ezen kívül kell még egy munkáltatói igazolás, ami mutatja, hogy van legalább 3 hónap munkaviszony, illetve letelt a 3-6 hónap próbaidő.",
					},
				},
				{
					"@type": "Question",
					name: "Lehet személyi kölcsönből pótolni egy lakáshitelnél az önerőt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Személyi kölcsön nem használható fel önerőként lakáshitelhez, ugyanis ez növelné a lakáshitelezés kockázatát. Ezért a lakáshitellel párhuzamosan, vagy az azt megelőző 3 hónapon belül igényelt személyi kölcsönök, fedezetlen hitelek felhasználását ellenőriznie kell a bankoknak. Legalábbis azt, hogy ezen összegek nem képezik-e részét a lakásvásárlás önerejének.",
					},
				},
				{
					"@type": "Question",
					name: "Nyugdíjasként vehetek fel személyi kölcsönt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A nyugdíj kiszámítható, stabil jövedelem, ezért a bankok elfogadják személyi kölcsön igénylésekor. A bankok ugyanakkor meghatároznak egy maximális életkort, ameddig hiteleznek, ez általában 65-70 év.",
					},
				},
				{
					"@type": "Question",
					name: "Meglévő hitelt kiválthatok személyi kölcsönnel?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A személyi kölcsön hitelkiváltásra is igényelhető, ám nem mindegy, hogy a kiváltandó hitel ugyanannál a banknál, vagy egy másiknál van-e. Amikor dedikáltan hitelkiváltás a cél, akkor a bank a kiváltandó hitel havi törlesztőjével már nem számol a JTM vizsgálat során, hiszen ezt a későbbiekben már nem kell fizetni.",
					},
				},
				{
					"@type": "Question",
					name: "Autóvásárlásra is kaphatok személyi kölcsönt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Igen, általában használt autó vásárlására igényelnek személyi kölcsönt. Mivel a személyi kölcsön szabad felhasználású hitel, akár egy külföldi tulajdonban lévő autó is megvásárolható ezzel a finanszírozási formával.",
					},
				},
			],
		};
	}
}
